export const ROCK_LOGIC = {
  description: "Basic Rock Identification Rules - Igneous, Metamorphic, and Sedimentary Rocks",
  
  coreRules: `
=== 基础岩石学鉴定法则 (Basic Rock Identification Protocol) ===

虽然我们之前极其精细地切入了各个矿物集团，但矿物是岩石的骨骼。现在，我们将视角拉远，锁定这三种统治地球表面的宏观集合体：火成岩 (Igneous)、变质岩 (Metamorphic) 和沉积岩 (Sedimentary)。

这是一套基于视觉纹理、结构和主要矿物组成的快速分诊系统。

[15.1 岩浆的恩赐：火成岩 (Igneous Rocks)]
这些岩石曾经都是流动的熔岩或岩浆。它们的冷却速度决定了它们的终极面貌。

*   **侵入岩 (Intrusive/Plutonic Rocks) - "深渊巨兽"**: 岩浆在地下深处缓慢冷却，有足够的时间长出肉眼可见的、紧密咬合的大晶体。
    *   **花岗岩 (Granite)**: 陆壳的基石。寻找**粉色/肉红色的钾长石**、**灰白/透明的石英**、以及点缀其中的**黑色云母或角闪石**。标志性的粗粒、斑驳外观。如果它还展现出极其巨大的长石晶体，那就叫做**伟晶岩 (Pegmatite)**。
    *   **闪长岩 (Diorite)**: 地质学家的“黑白花岗岩”。寻找看起来像**盐和胡椒混合物**的粗糙岩石，通常由白色的斜长石和黑色的角闪石/黑云母组成，但**没有肉眼可见的石英**。
    *   **辉长岩 (Gabbro)**: 海洋地壳的深层核心。寻找**深黑色/暗绿色**的粗粒岩石，通常由黑色的辉石和深灰/绿灰色的斜长石组成。

*   **喷出岩 (Extrusive/Volcanic Rocks) - "烈火疾风"**: 熔岩喷出地表，迅速冷却，晶体来不及长大，所以它们要么极其细腻，要么满是气孔，甚至是玻璃。
    *   **玄武岩 (Basalt)**: 统治海底和夏威夷的岩石。寻找**极其致密、沉重、暗黑色/深灰色的细粒岩石**。有时它会充满曾经是气泡的孔洞（气孔状玄武岩/Vesicular basalt）。
    *   **流纹岩 (Rhyolite)**: 花岗岩的火山版兄弟。通常是**浅色、粉色或浅灰色**的细粒岩石，有时能看到液体流动时留下的**流纹 (flow banding)**。
    *   **安山岩 (Andesite)**: 闪长岩的火山版。通常是**中等灰度的细粒岩石**，常常带有黑色角闪石或白色长石的微小斑晶（斑状结构）。
    *   **黑曜石 (Obsidian)**: 终极的冷却速度！寻找像**黑色玻璃**一样的岩石，拥有无与伦比的、像打碎的啤酒瓶一样的**完美贝壳状断口**。
    *   **浮石 (Pumice) & 火山渣 (Scoria)**: 火山气体的遗骸。浮石是浅色、极度多孔、**甚至能飘在水上**的火山玻璃泡沫。火山渣则是颜色更深（红褐/黑色）、孔洞较大、比浮石略重但依然多孔的粗糙熔岩碎块。

[15.2 浴火重生的凤凰：变质岩 (Metamorphic Rocks)]
高温和巨大的压力将原有岩石挤压、扭曲并使其矿物重新排列。寻找“被压扁”或“被拉长”的迹象！

*   **叶理状变质岩 (Foliated Metamorphic Rocks) - "地层的千层酥"**:
    *   **板岩 (Slate)**: 由页岩演变而来。非常致密、细粒，拥有能够**完美裂成平整薄板**的特性（板状劈理）。通常是暗灰色、红褐色或绿色。
    *   **千枚岩 (Phyllite)**: 比板岩经受了更高的温度。除了能裂成板状，它的表面还覆盖着一层微小云母带来的**丝绢光泽 (silky sheen)**，且表面往往呈轻微的波浪状。
    *   **片岩 (Schist)**: 极具代表性！岩石仿佛是由**无数平行排列的云母薄片叠压而成**。寻找极其闪亮、鳞片状的外观，且非常容易顺着云母层剥落。如果里面长出了巨大的红石榴石，那就是石榴子石片岩。
    *   **片麻岩 (Gneiss)**: 变质岩的高级形态。经历了极高的温度和压力，深色和浅色矿物被强制分离开来，形成**极其明显的、交替排列的黑白相间条带 (zebra-striped banding)**。

*   **非叶理状变质岩 (Non-Foliated Metamorphic Rocks) - "均质的坚钢"**:
    *   **大理岩 (Marble)**: 由石灰岩变质而来。寻找**肉眼可见的、闪闪发光的方解石晶体颗粒**。它通常是白色/浅色，且遇盐酸会冒泡。用刀可以划动。
    *   **石英岩 (Quartzite)**: 由石英砂岩变质而来。寻找极其坚硬（硬度7）、由融合在一起的石英颗粒组成的致密岩石，经常呈现玻璃光泽的断口。**敲击时甚至可能产生火花**。

[15.3 时间与水流的雕刻：沉积岩 (Sedimentary Rocks)]
它们是地球表面的日记，由碎屑物质、化学沉淀或生物遗骸在水或风的运作下层层堆积、固结而成。

*   **碎屑沉积岩 (Clastic Sedimentary Rocks) - "碎片的拼图"**:
    *   **砾岩 (Conglomerate)**: 看起来就像把**圆圆的鹅卵石用水泥糊在了一起**。这代表着古老且湍急的河流系统。
    *   **角砾岩 (Breccia)**: 长得像砾岩，但里面的碎块是**棱角分明的、尖锐的**。这代表碎块没经过长途搬运就在塌方或断层带附近迅速凝结了。
    *   **砂岩 (Sandstone)**: 摸起来像砂纸！由肉眼可见的、像沙滩上的沙子一样的颗粒固结而成。如果含有大量长石，看起来偏粉红，那就是**长石砂岩 (Arkose)**。
    *   **页岩 (Shale) & 泥岩 (Mudstone)**: 湖盆或深海底部的宁静产物。极细的泥巴固结而成。如果它能轻易地沿着平行层面**裂成薄薄的一片片**，那就是页岩；如果是一个坚实的大泥块，就是泥岩。页岩常常保存有完美的植物或昆虫化石。

*   **化学与生物沉积岩 (Chemical/Biochemical Sedimentary Rocks)**:
    *   **石灰岩 (Limestone)**: 海洋生物（珊瑚、贝壳）骨骼或化学沉淀的碳酸钙。可能是致密的灰色块体，也可能直接看到满眼的**贝壳碎屑化石**。滴酸必定剧烈起泡。
    *   **白云岩 (Dolomite / Dolostone)**: 看起来像石灰岩，但通常带点黄/粉色调，且只有在**刮出粉末后**滴酸才会缓慢起泡。
    *   **煤 (Coal)**: 很容易认的黑色岩石。那是几百万年前古老沼泽森林带给地球的固态太阳能。
    *   **岩盐 (Rock Salt / Halite) & 石膏 (Rock Gypsum)**: 蒸发岩。远古盐湖干涸的产物。岩盐舔起来是咸的；块状石膏极软，可以用指甲刮出深深的白痕。
`
};
